import {
  Button,
  IconButton,
  Tooltip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from '@mui/material';
import { PictureAsPdfRounded } from '@mui/icons-material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { pdf } from '@react-pdf/renderer';
import { openGeneratedPdf } from '../../../lib/open-generated-pdf';
import { renderWithoutHyphenation } from '../../../lib/pdf-hyphenation';
import { SyllabusPdfDocument, type SyllabusPdfData } from './SyllabusPdfDocument';
import type { TextbookDto } from '../../../lib/api/course-template.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { loadComposedSyllabus, loadTemplate } from '../../../lib/composer/load-composed-syllabus';
import { normalizeExpectedHours } from '@dosfilos/domain';
import { buildSyllabusPdfLabels } from '../../../lib/syllabus-pdf-labels';
import {
  materializeModuleSchedule,
  synthesizeDeadlines,
  type RichModule,
  type RichSessionFormat,
  type RichAssignment,
} from '../../../lib/syllabus-pdf-preview-data';

// ─── Rich template shape from the editor (full module structure) ────────────

interface Props {
  /** Sólo el ícono, con el texto en un tooltip: la barra de acciones no entra
   *  entera en pantallas angostas. */
  compact?: boolean;
  /** The editor holds changes Firestore does not have yet. */
  hasUnsavedChanges?: boolean;
  /** Persists the template; resolves once saved. */
  /** Persists the template; resolves to whether it actually saved. */
  onSaveFirst?: () => Promise<boolean>;
  template: {
    title: string;
    code?: string;
    description: Record<string, unknown>;
    objectives: SyllabusPdfData['template']['objectives'];
    philosophy: SyllabusPdfData['template']['philosophy'];
    grading: SyllabusPdfData['template']['grading'];
    readings: SyllabusPdfData['template']['readings'];
    sessionFormat: SyllabusPdfData['template']['sessionFormat'];
    modules: SyllabusPdfData['template']['modules'] & RichModule[];
    assignments: SyllabusPdfData['template']['assignments'] & RichAssignment[];
    latePolicy?: SyllabusPdfData['template']['latePolicy'] | undefined;
    anchorVerse?: SyllabusPdfData['template']['anchorVerse'] | undefined;
    biblicalVersion?: string;
  };
  textbooks: TextbookDto[];
  syllabus?: {
    instructor: { name: string; credentials?: string };
    termRange: { startDate: string; endDate: string };
    modality: string;
  };
  tenantName?: string;
  documentLogoUrl?: string;
  mission?: { text: string; scripture?: string };
  programName?: string;
  programPurpose?: string;
  /** Rubrics referenced by template assignments (Phase C.1). */
  referencedRubrics?: SyllabusPdfData['referencedRubrics'];
  /** Template id — required to load the persisted template + run the composer. */
  templateId?: string;
}

export function SyllabusPdfPreviewButton({
  compact,
  hasUnsavedChanges,
  onSaveFirst,
  template,
  textbooks,
  syllabus,
  tenantName,
  documentLogoUrl,
  mission,
  programName,
  programPurpose,
  referencedRubrics,
  templateId,
}: Props): JSX.Element {
  const [busy, setBusy] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const { currentTenantId } = useTenantStore();
  const { t, i18n } = useTranslation('sis');
  // Resolved HERE, in a component under the i18next provider, because the
  // document is rendered by react-pdf outside this React tree.
  const labels = buildSyllabusPdfLabels(t, i18n.language);

  /**
   * The preview reads the PERSISTED template and runs the composer over it, then
   * merges the editor's in-memory state. Because the composer falls back to the
   * persisted template, the composed value wins for description, objectives and
   * the anchor verse — so unsaved edits to THOSE fields never appear, while
   * unsaved edits to readings or modules do. Rather than explain that split to
   * the author, the preview refuses to show a half-truth: it offers to save
   * first, or to preview exactly what is stored.
   */
  const handleClick = async () => {
    if (hasUnsavedChanges && onSaveFirst) {
      setConfirmOpen(true);
      return;
    }
    void runPreview();
  };

  const runPreview = async () => {
    setBusy(true);
    try {
      // The tab opens now, inside the click; the PDF is generated into it.
      await openGeneratedPdf(async () => {
        // Phase E: pull the persisted template + run the composer so the PDF
        // reflects the live Tenant/Department/Program/Course chain rather than
        // the editor's local state for legacy fields.
        let composed: SyllabusPdfData['composed'] | undefined;
        if (currentTenantId && templateId) {
          try {
            const tpl = await loadTemplate(currentTenantId, templateId);
            if (tpl) {
              const c = await loadComposedSyllabus({ tenantId: currentTenantId, template: tpl });
              composed = {
                ...(c.departmentName !== undefined ? { departmentName: c.departmentName } : {}),
                ...(c.departmentPurpose !== undefined
                  ? { departmentPurpose: c.departmentPurpose }
                  : {}),
                // Program name/purpose come straight off the resolved program
                // so the PDF never falls back to a stale or absent caller value.
                ...(c.programName !== undefined ? { programName: c.programName } : {}),
                ...(c.programPurpose !== undefined ? { programPurpose: c.programPurpose } : {}),
                departmentObjectives: (c.departmentObjectives ?? []).map((o) => ({
                  id: o.id,
                  ...(o.code ? { code: o.code } : {}),
                  text: o.text,
                })),
                programObjectives: (c.programObjectives ?? []).map((o) => ({
                  id: o.id,
                  ...(o.code ? { code: o.code } : {}),
                  text: o.text,
                })),
                courseObjectives: (c.courseObjectives ?? []).map((o) => ({
                  id: o.id,
                  ...(o.code ? { code: o.code } : {}),
                  text: o.text,
                })),
                description: c.description,
                ...(c.anchorVerse ? { anchorVerse: c.anchorVerse } : {}),
                ...(c.biblicalVersion ? { biblicalVersion: c.biblicalVersion } : {}),
                philosophy: c.philosophy,
              };
            }
          } catch (composeErr) {
            // Composer is best-effort: if Firestore reads fail, fall back to
            // the template's legacy fields. Log so devs can investigate.
            // eslint-disable-next-line no-console
            console.warn('[SyllabusPdf] composeSyllabus failed; falling back', composeErr);
          }
        }

        const tbMap: SyllabusPdfData['textbookMap'] = {};
        for (const tb of textbooks) {
          tbMap[tb.id] = {
            title: tb.title,
            authors: tb.authors,
            ...(tb.year ? { year: tb.year } : {}),
            ...(tb.publisher ? { publisher: tb.publisher } : {}),
          };
        }
        const today = new Date();
        const sixtyFromNow = new Date(today.getTime() + 60 * 24 * 60 * 60 * 1000);
        const isoDate = (d: Date) => d.toISOString().slice(0, 10);
        const data: SyllabusPdfData = {
          template: {
            ...template,
            // Legacy fixed-shape hours → dynamic list (raw template may be old).
            grading: {
              ...template.grading,
              expectedHours: normalizeExpectedHours(template.grading.expectedHours),
            },
          } as SyllabusPdfData['template'],
          syllabus: syllabus ?? {
            instructor: { name: labels.instructorToBeAssigned },
            termRange: { startDate: isoDate(today), endDate: isoDate(sixtyFromNow) },
            modality: 'online',
          },
          textbookMap: tbMap,
          moduleSchedule: materializeModuleSchedule(
            template.modules as RichModule[],
            template.sessionFormat as RichSessionFormat,
            labels,
          ),
          deadlines: synthesizeDeadlines(template.assignments as RichAssignment[], labels),
          tenantName: tenantName ?? labels.tenantFallback,
          ...(documentLogoUrl ? { documentLogoUrl } : {}),
          ...(mission ? { mission } : {}),
          ...(programName ? { programName } : {}),
          ...(programPurpose ? { programPurpose } : {}),
          ...(referencedRubrics && referencedRubrics.length > 0 ? { referencedRubrics } : {}),
          ...(composed ? { composed } : {}),
        };
        // Diagnostic: surface why a rubric section may not appear.
        if (!referencedRubrics || referencedRubrics.length === 0) {
          // eslint-disable-next-line no-console
          console.warn(
            '[SyllabusPdf] No rubrics in PDF: assignments lack `rubricRef` or the program/tenant has no rubrics in its catalog. Set a rubric per assignment in the editor → Assignments tab.',
          );
        }
        // Without hyphenation: @react-pdf splits Spanish words with English rules.
        return renderWithoutHyphenation(() =>
          pdf(<SyllabusPdfDocument data={data} labels={labels} />).toBlob(),
        );
      }, 'syllabus');
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {/* En pantalla angosta el botón se queda con su ícono y el texto pasa al
          tooltip: tres botones de texto en una fila sin `wrap` desbordan la barra
          y empujan el título fuera de la pantalla. El `aria-label` conserva la
          misma palabra para quien navega por lector de pantalla. */}
      {compact ? (
        <Tooltip title={labels.previewButton}>
          <span>
            <IconButton
              color="primary"
              aria-label={labels.previewButton}
              onClick={handleClick}
              disabled={busy}
            >
              {busy ? <CircularProgress size={20} /> : <PictureAsPdfRounded />}
            </IconButton>
          </span>
        </Tooltip>
      ) : (
        <Button
          variant="outlined"
          startIcon={busy ? <CircularProgress size={16} /> : <PictureAsPdfRounded />}
          onClick={handleClick}
          disabled={busy}
        >
          {labels.previewButton}
        </Button>
      )}
      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{t('templateEditor.preview.unsavedTitle')}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('templateEditor.preview.unsavedBody')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)}>{t('common:actions.cancel')}</Button>
          <Button
            onClick={() => {
              setConfirmOpen(false);
              void runPreview();
            }}
          >
            {t('templateEditor.preview.previewSaved')}
          </Button>
          <Button
            variant="contained"
            onClick={async () => {
              setConfirmOpen(false);
              setBusy(true);
              let saved = false;
              try {
                saved = await onSaveFirst!();
              } finally {
                setBusy(false);
              }
              // A failed save leaves the stored template stale. Previewing anyway
              // would open exactly the half-truth this dialog exists to prevent;
              // the editor already shows the error.
              if (saved) void runPreview();
            }}
          >
            {t('templateEditor.preview.saveAndPreview')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
